import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { cleanupAuthState, secureSignOut } from "@/utils/authUtils";

const Admin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Check if user is already authenticated or if this is a password reset
  useEffect(() => {
    // Check URL params for our custom reset token
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const type = urlParams.get('type');

    if (token && type === 'password-reset') {
      console.log('Custom password reset token detected:', token);
      // Clean the URL and show password reset form
      navigate('/admin', { replace: true });
      setResetToken(token);
      setIsPasswordReset(true);
      toast({
        title: "Lösenordsåterställning",
        description: "Ange ditt nya lösenord nedan.",
      });
      return;
    }

    const checkSessionAndRecovery = async () => {
      try {
        // Check URL hash for auth parameters (magic links)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const hashType = hashParams.get('type');
        const error = hashParams.get('error');
        const errorDescription = hashParams.get('error_description');

        // Also check for token_hash (new format)
        const urlParams = new URLSearchParams(window.location.search);
        const directToken = urlParams.get('token');
        const directType = urlParams.get('type');
        
        // Also check for token_hash (new format)
        const tokenHash = urlParams.get('token_hash');
        const emailType = urlParams.get('type');

        console.log('Auth check - Hash params:', { 
          type: hashType, 
          accessToken: !!accessToken, 
          refreshToken: !!refreshToken, 
          error, 
          errorDescription,
          tokenHash: !!tokenHash,
          emailType,
          directToken: !!directToken,
          directType,
          fullHashParams: Object.fromEntries(hashParams.entries()),
          fullUrlParams: Object.fromEntries(urlParams.entries())
        });

        // Handle errors first (like expired tokens)
        if (error) {
          const isTokenExpired = errorDescription?.includes('Email link is invalid') || 
                                 error === 'access_denied' || 
                                 hashParams.get('error_code') === 'otp_expired';
          
          if (isTokenExpired) {
            toast({
              title: "Återställningslänk har gått ut",
              description: "Lösenordsåterställningslänken har gått ut eller redan använts. Begär en ny återställningslänk nedan.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Fel",
              description: errorDescription || "Ett oväntat fel inträffade",
              variant: "destructive",
            });
          }
          
          navigate('/admin', { replace: true });
          return;
        }

        // Handle email confirmation with token_hash (for invitations)
        if (tokenHash && emailType === 'invite') {
          console.log('Email invite confirmation detected with token_hash:', tokenHash);
          try {
            const { data, error: verifyError } = await supabase.auth.verifyOtp({
              type: 'invite',
              token_hash: tokenHash,
            });
            
            if (verifyError) {
              console.error('Invite verification error:', verifyError);
              
              if (verifyError.message?.includes('expired') || verifyError.message?.includes('invalid') || verifyError.status === 403) {
                toast({
                  title: "Inbjudan har gått ut",
                  description: "Inbjudningslänken har gått ut eller redan använts. Be administratören skicka en ny inbjudan.",
                  variant: "destructive",
                });
              } else {
                toast({
                  title: "Fel",
                  description: `Misslyckades att bearbeta inbjudan: ${verifyError.message}`,
                  variant: "destructive",
                });
              }
              
              navigate('/admin', { replace: true });
            } else {
              console.log('Invite verification successful, showing password setup');
              navigate('/admin', { replace: true });
              setIsPasswordReset(true);
              toast({
                title: "Välkommen!",
                description: "Välj ett lösenord för att slutföra din kontoregistrering.",
              });
            }
          } catch (error) {
            console.error('Invite verification error:', error);
            toast({
              title: "Inbjudan har gått ut",
              description: "Inbjudningslänken har gått ut eller redan använts. Be administratören skicka en ny inbjudan.",
              variant: "destructive",
            });
            navigate('/admin', { replace: true });
          }
          return;
        }

        // If not a recovery/invite flow, check for a normal session
        const { data: { session } } = await supabase.auth.getSession();
        if (session && !isPasswordReset) {
          console.log('Existing session found, redirecting to dashboard');
          navigate("/admin/dashboard");
        } else {
          // Check if there are any leftover auth parameters that weren't handled
          if (hashType || accessToken || refreshToken || tokenHash) {
            console.log('Found unhandled auth parameters, cleaning URL');
            navigate('/admin', { replace: true });
          }
        }
      } catch (error) {
        console.error("Error during auth check:", error);
        cleanupAuthState();
      }
    };

    checkSessionAndRecovery();
  }, [location.hash, navigate, toast, isPasswordReset]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      cleanupAuthState();
      
      try {
        await secureSignOut();
      } catch (signOutError) {
        console.error("Sign out failed, continuing with sign in", signOutError);
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      window.location.href = "/admin/dashboard";
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Inloggning misslyckades",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        title: "E-post krävs",
        description: "Vänligen ange din e-postadress först",
        variant: "destructive",
      });
      return;
    }

    setIsResetting(true);
    try {
      // Route via custom SMTP edge function (fosforskning.se) — the Lovable
      // Email domain notify.info.fosforskning.se is still pending DNS, so
      // Supabase's native /recover flow currently cannot deliver mail.
      const { error } = await supabase.functions.invoke('send-password-reset', {
        body: { email },
      });

      console.log('Password reset request sent via send-password-reset to:', email);

      if (error) {
        toast({
          title: "Fel",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Återställningsmail skickat",
        description: "Kontrollera din e-post för instruktioner att återställa lösenord",
      });
    } catch (error) {
      console.error("Password reset error:", error);
      toast({
        title: "Fel",
        description: "Ett oväntat fel inträffade",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Fel",
        description: "Lösenorden matchar inte",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Fel",
        description: "Lösenordet måste vara minst 6 tecken långt",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      if (resetToken) {
        // Use our custom password reset completion
        const { error } = await supabase.functions.invoke('complete-password-reset', {
          body: { 
            token: resetToken, 
            newPassword: newPassword 
          }
        });

        if (error) {
          toast({
            title: "Fel",
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Lösenord uppdaterat",
          description: "Ditt lösenord har uppdaterats. Du kan nu logga in.",
        });

        setIsPasswordReset(false);
        setResetToken(null);
      } else {
        // Regular password update for authenticated users
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (error) {
          toast({
            title: "Fel",
            description: error.message,
            variant: "destructive",
          });
          return;
        }

        toast({
          title: "Lösenord uppdaterat",
          description: "Ditt lösenord har uppdaterats. Vänligen logga in igen.",
        });

        await secureSignOut();
        setIsPasswordReset(false);
      }
      
    } catch (error: any) {
      console.error("Password update error:", error);
      toast({
        title: "Fel",
        description: error.message || "Ett oväntat fel inträffade",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (isPasswordReset) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-6 space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Återställ lösenord
            </h1>
            <p className="text-sm text-muted-foreground">
              Ange ditt nya lösenord nedan
            </p>
          </div>

          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nytt lösenord</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isUpdatingPassword}
            >
              {isUpdatingPassword ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Uppdaterar...</span>
                </div>
              ) : (
                "Uppdatera lösenord"
              )}
            </Button>
          </form>

          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setIsPasswordReset(false);
              setResetToken(null);
              cleanupAuthState();
              window.location.href = "/admin";
            }}
          >
            Tillbaka till inloggning
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Gruppledare inloggning
          </h1>
          <p className="text-sm text-muted-foreground">
            Logga in för att komma åt gruppledarpanelen
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-post</Label>
            <Input
              id="email"
              type="email"
              placeholder="din.epost@exempel.se"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Lösenord</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loggar in...</span>
              </div>
            ) : (
              "Logga in"
            )}
          </Button>

          <Button 
            type="button" 
            variant="ghost" 
            className="w-full text-sm" 
            onClick={handleForgotPassword}
            disabled={isResetting}
          >
            {isResetting ? "Skickar..." : "Glömt lösenord?"}
          </Button>
        </form>

        <div className="text-center">
          <Button
            variant="link"
            className="text-sm text-muted-foreground"
            onClick={() => navigate("/admin/register")}
          >
            Registrera dig som gruppledare
          </Button>
        </div>

        <Button
          variant="ghost"
          className="w-full"
          onClick={() => navigate("/")}
        >
          Tillbaka till startsidan
        </Button>
      </Card>
    </div>
  );
};

export default Admin;
